import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, List, Table, TableBody, TableCell, TableHead, TableRow, Chip, Alert } from '@mui/material';
import { ExpandMore, AccountTree } from '@mui/icons-material';
import { SectionHeader, Done, Todo } from './DocPrimitives';
import { PIPELINES, PIPELINE_STAGES_DONE, PIPELINE_STAGES_TODO, CROSS_ACCOUNT_DONE, CROSS_ACCOUNT_TODO, MOBILE_BUILDS_DONE, MOBILE_BUILDS_TODO } from '../../data/docs/cicd';

export default function CicdTab() {
  return (
    <Box>
      <SectionHeader icon={<AccountTree />} title="CI/CD Pipeline" subtitle="AWS CodePipeline — separate pipelines for backend infrastructure and frontend portal" />

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Pipeline Overview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Two independent pipelines are defined under <code>cicd/</code>. Each covers three environments: Dev (no gates), UAT (security + approval), and Prod (security + approval). All pipelines run in the CICD account (<code>322156488575</code>) and deploy cross-account.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Pipeline</strong></TableCell>
                <TableCell><strong>Repo</strong></TableCell>
                <TableCell><strong>Branch → Env</strong></TableCell>
                <TableCell><strong>Target account</strong></TableCell>
                <TableCell><strong>Approval</strong></TableCell>
                <TableCell><strong>Security</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {PIPELINES.map((p, i) => (
                <TableRow key={i}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell><code>{p.repo}</code></TableCell>
                  <TableCell><code>{p.branch}</code></TableCell>
                  <TableCell><code>{p.account}</code></TableCell>
                  <TableCell><Chip label={p.approval} size="small" color={p.approval === 'None' ? 'default' : 'warning'} /></TableCell>
                  <TableCell><Chip label={p.security} size="small" color={p.security === 'Enabled' ? 'success' : 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">All three environments (Dev, UAT, Prod) are fully configured with cross-account deployment roles.</Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Pipeline Stages</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List dense disablePadding>
            {PIPELINE_STAGES_DONE.map((item, i) => <Done key={i}>{item}</Done>)}
            {PIPELINE_STAGES_TODO.map((item, i) => <Todo key={i}>{item}</Todo>)}
          </List>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Cross-Account Roles</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" mb={2}>
            The pipeline runs in the infrastructure account (<code>322156488575</code>) and assumes deployment roles in the target account. Roles are defined in <code>cicd/*/lib/cross-account/</code>.
          </Typography>
          <List dense disablePadding>
            {CROSS_ACCOUNT_DONE.map((item, i) => <Done key={i}>{item}</Done>)}
            {CROSS_ACCOUNT_TODO.map((item, i) => <Todo key={i}>{item}</Todo>)}
          </List>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Mobile App Builds (TestFlight / Internal)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Mobile apps use EAS (Expo Application Services) for builds. A separate <code>cicd/testflight/</code> pipeline handles iOS TestFlight distribution.
          </Typography>
          <List dense disablePadding>
            {MOBILE_BUILDS_DONE.map((item, i) => <Done key={i}>{item}</Done>)}
            {MOBILE_BUILDS_TODO.map((item, i) => <Todo key={i}>{item}</Todo>)}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
